import { useEffect, useState } from "react";
import type { Panel } from "@electro-planner/domain";

/**
 * Dialog för projekt-metadata: konstruktör, företag, telefon, plats,
 * kapsling och fria anteckningar. Syns i toolbaren och i PDF-titelblock.
 */
export function ProjectInfoDialog({
  open,
  panel,
  onClose,
  onChange,
}: {
  open: boolean;
  panel: Panel | undefined;
  onClose: () => void;
  onChange: (patch: Partial<Omit<Panel, "id" | "createdAt">>) => void;
}) {
  const [local, setLocal] = useState<{
    designer?: string;
    company?: string;
    phone?: string;
    location?: string;
    enclosure?: string;
    projectNotes?: string;
  }>({});

  useEffect(() => {
    if (!open) return;
    setLocal({
      designer: panel?.designer,
      company: panel?.company,
      phone: panel?.phone,
      location: panel?.location,
      enclosure: panel?.enclosure,
      projectNotes: panel?.projectNotes,
    });
  }, [open, panel]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const save = () => {
    onChange({
      designer: local.designer?.trim() || undefined,
      company: local.company?.trim() || undefined,
      phone: local.phone?.trim() || undefined,
      location: local.location?.trim() || undefined,
      enclosure: local.enclosure?.trim() || undefined,
      projectNotes: local.projectNotes?.trim() || undefined,
    });
    onClose();
  };

  const field = (
    key: keyof typeof local,
    label: string,
    placeholder?: string,
  ) => (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-slate-700">{label}</span>
      <input
        type="text"
        value={local[key] ?? ""}
        placeholder={placeholder}
        onChange={(e) => setLocal((s) => ({ ...s, [key]: e.target.value }))}
        className="rounded border border-slate-300 px-2 py-1 text-sm"
      />
    </label>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[80vh] w-full max-w-xl flex-col overflow-hidden rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 className="text-lg font-semibold text-slate-800">
            Projektinformation
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded px-2 py-1 text-slate-500 hover:bg-slate-100"
            aria-label="Stäng"
          >
            ×
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3 overflow-auto p-4">
          {field("designer", "Konstruktör", "Ditt namn")}
          {field("company", "Företag", "Företagsnamn AB")}
          {field("phone", "Telefon", "070-123 45 67")}
          {field("location", "Plats", "El-rum källare")}
          <div className="col-span-2">
            {field("enclosure", "Kapsling", "ABB Mistral41W IP55")}
          </div>
          <label className="col-span-2 flex flex-col gap-1">
            <span className="text-xs font-medium text-slate-700">Anteckningar</span>
            <textarea
              value={local.projectNotes ?? ""}
              onChange={(e) =>
                setLocal((s) => ({ ...s, projectNotes: e.target.value }))
              }
              rows={4}
              className="rounded border border-slate-300 px-2 py-1 text-sm"
              placeholder="Fri text — syns i PDF-titelblock."
            />
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-slate-300 bg-white px-3 py-1 text-sm hover:bg-slate-50"
          >
            Avbryt
          </button>
          <button
            type="button"
            onClick={save}
            className="rounded bg-sky-600 px-3 py-1 text-sm text-white hover:bg-sky-700"
          >
            Spara
          </button>
        </div>
      </div>
    </div>
  );
}
